import React, { useState } from "react";
import {
  Calendar,
  Search,
  ArrowDownCircle,
  ArrowUpCircle,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Settings as SettingsIcon,
} from "lucide-react";
import {
  TextField,
  Button,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Menu,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const PointsHistory = () => {
  // State for filters
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [transactionType, setTransactionType] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Mock data for transaction history
  const [transactionHistory, setTransactionHistory] = useState([
    {
      id: "TXN123456",
      customerId: 1,
      customerName: "John Doe",
      type: "earn",
      amount: 25,
      source: "Purchase #ORD-1234",
      date: "2023-03-22",
      description: "Points earned for product purchase",
      balance: 250,
    },
    {
      id: "TXN123457",
      customerId: 2,
      customerName: "Jane Smith",
      type: "earn",
      amount: 50,
      source: "Referral Bonus",
      date: "2023-03-21",
      description: "Points earned for referring a new customer",
      balance: 180,
    },
    {
      id: "TXN123458",
      customerId: 3,
      customerName: "Michael Johnson",
      type: "earn",
      amount: 30,
      source: "Purchase #ORD-1235",
      date: "2023-03-20",
      description: "Points earned for product purchase",
      balance: 350,
    },
    {
      id: "TXN123459",
      customerId: 1,
      customerName: "John Doe",
      type: "redeem",
      amount: 100,
      source: "Discount Voucher",
      date: "2023-03-19",
      description: "Points redeemed for a 10% discount voucher",
      balance: 225,
    },
    {
      id: "TXN123460",
      customerId: 4,
      customerName: "Emily Brown",
      type: "earn",
      amount: 15,
      source: "Purchase #ORD-1236",
      date: "2023-03-18",
      description: "Points earned for product purchase",
      balance: 120,
    },
    {
      id: "TXN123461",
      customerId: 5,
      customerName: "David Wilson",
      type: "redeem",
      amount: 80,
      source: "Free Product",
      date: "2023-03-17",
      description: "Points redeemed for a free product",
      balance: 420,
    },
    {
      id: "TXN123462",
      customerId: 2,
      customerName: "Jane Smith",
      type: "earn",
      amount: 20,
      source: "Purchase #ORD-1237",
      date: "2023-03-16",
      description: "Points earned for product purchase",
      balance: 130,
    },
    {
      id: "TXN123463",
      customerId: 3,
      customerName: "Michael Johnson",
      type: "adjustment",
      amount: 50,
      source: "Customer Support",
      date: "2023-03-15",
      description: "Points adjusted due to system error",
      balance: 320,
    },
    {
      id: "TXN123464",
      customerId: 5,
      customerName: "David Wilson",
      type: "earn",
      amount: 100,
      source: "Special Promotion",
      date: "2023-03-14",
      description: "Bonus points for participating in promotion",
      balance: 500,
    },
    {
      id: "TXN123465",
      customerId: 4,
      customerName: "Emily Brown",
      type: "earn",
      amount: 25,
      source: "Purchase #ORD-1238",
      date: "2023-03-13",
      description: "Points earned for product purchase",
      balance: 105,
    },
    {
      id: "TXN123466",
      customerId: 1,
      customerName: "John Doe",
      type: "adjustment",
      amount: -10,
      source: "Order Cancellation",
      date: "2023-03-12",
      description: "Points reversed due to order cancellation",
      balance: 325,
    },
    {
      id: "TXN123467",
      customerId: 2,
      customerName: "Jane Smith",
      type: "redeem",
      amount: 150,
      source: "Premium Membership",
      date: "2023-03-11",
      description: "Points redeemed for 1-month premium membership",
      balance: 110,
    },
  ]);

  // Handle filter menu open/close
  const handleFilterClick = event => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Filter transactions based on filters
  const filteredTransactions = transactionHistory.filter(transaction => {
    const matchesSearch =
      transaction.customerName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      transaction.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.source.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType =
      transactionType === "all" || transaction.type === transactionType;

    // Date range filter logic
    let matchesDateRange = true;
    if (dateRange.startDate && dateRange.endDate) {
      const transactionDate = new Date(transaction.date);
      matchesDateRange =
        transactionDate >= dateRange.startDate &&
        transactionDate <= dateRange.endDate;
    }

    return matchesSearch && matchesType && matchesDateRange;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Download report function
  const handleDownloadReport = () => {
    // Logic to download transaction history as CSV/PDF
    alert("Download report functionality will be implemented here");
  };

  return (
    <div className="bg-white h-full flex flex-col">
      {/* Header Section - Updated to match image 1 */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
        <h1 className="text-2xl font-semibold font-outfit text-[#374151]">
          Points Transaction History
        </h1>
        <div className="flex items-center gap-2">
          <IconButton
            sx={{
              color: "#5652B0",
              border: "1px solid #E5E7EB",
              borderRadius: "4px",
              p: "8px",
            }}>
            <RefreshCw size={18} />
          </IconButton>
          <IconButton
            sx={{
              color: "#5652B0",
              border: "1px solid #E5E7EB",
              borderRadius: "4px",
              p: "8px",
            }}>
            <SettingsIcon size={18} />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<Download size={18} />}
            onClick={handleDownloadReport}
            sx={{
              borderColor: "#5652B0",
              color: "#5652B0",
              "&:hover": { borderColor: "#4B47A1", backgroundColor: "#F5F3FF" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}>
            Download Report
          </Button>
        </div>
      </div>

      {/* Search Bar - Updated to match image 1 & 3 */}
      <div className="flex justify-between items-center py-4 px-6 border-b border-[#E5E7EB] bg-white">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex items-center w-full max-w-md">
            <Search className="absolute left-3 text-gray-400" size={20} />
            <TextField
              placeholder="Search by customer or transaction"
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  paddingLeft: "40px",
                  borderRadius: "8px",
                  backgroundColor: "#F9FAFB",
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Filter Controls - Similar to image 3 */}
      <div className="flex items-center gap-4 py-4 px-6 border-b border-[#E5E7EB]">
        <div className="flex flex-col">
          {/* <span className="text-sm text-gray-500 mb-1">Transaction Type</span> */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={transactionType}
              onChange={e => setTransactionType(e.target.value)}
              displayEmpty
              sx={{ borderRadius: "8px" }}>
              <MenuItem value="all">All Transactions</MenuItem>
              <MenuItem value="earn">Points Earned</MenuItem>
              <MenuItem value="redeem">Points Redeemed</MenuItem>
              <MenuItem value="adjustment">Adjustments</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="flex flex-col">
          {/* <span className="text-sm text-gray-500 mb-1">From Date</span> */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={dateRange.startDate}
              onChange={date => setDateRange({ ...dateRange, startDate: date })}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    width: 150,
                    "& .MuiInputBase-root": { borderRadius: "8px" },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </div>

        <div className="flex flex-col">
          {/* <span className="text-sm text-gray-500 mb-1">To Date</span> */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={dateRange.endDate}
              onChange={date => setDateRange({ ...dateRange, endDate: date })}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    width: 150,
                    "& .MuiInputBase-root": { borderRadius: "8px" },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </div>
      </div>

      {/* Transactions List */}
      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#F9FAFB]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Points
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedTransactions.map(transaction => (
                <tr key={transaction.id} className="hover:bg-[#F9FAFB]">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <span>{transaction.id}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {transaction.customerName}
                        </div>
                        {/* <div className="text-sm text-gray-500">
                          ID: {transaction.customerId}
                        </div> */}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar size={14} className="mr-1 text-gray-400" />
                      {transaction.date}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {transaction.type === "earn" && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Earned
                      </span>
                    )}
                    {transaction.type === "redeem" && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Redeemed
                      </span>
                    )}
                    {transaction.type === "adjustment" && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Adjustment
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className={`flex items-center font-medium ${
                        transaction.type === "earn"
                          ? "text-green-600"
                          : transaction.type === "redeem"
                          ? "text-blue-600"
                          : transaction.amount >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}>
                      {transaction.type === "earn" && (
                        <ArrowUpCircle size={16} className="mr-1" />
                      )}
                      {transaction.type === "redeem" && (
                        <ArrowDownCircle size={16} className="mr-1" />
                      )}
                      {transaction.type === "adjustment" &&
                        (transaction.amount >= 0 ? (
                          <ArrowUpCircle size={16} className="mr-1" />
                        ) : (
                          <ArrowDownCircle size={16} className="mr-1" />
                        ))}
                      {transaction.amount >= 0 ? "+" : ""}
                      {transaction.amount}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {transaction.source}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {transaction.balance}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {paginatedTransactions.length === 0 && (
            <div className="py-10 text-center">
              <p className="text-gray-500">
                No transactions found with the given search criteria
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {filteredTransactions.length > 0 && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
          <div className="flex items-center text-sm text-gray-500">
            Showing {startIndex + 1} to{" "}
            {Math.min(startIndex + itemsPerPage, filteredTransactions.length)}{" "}
            of {filteredTransactions.length} entries
          </div>
          <div className="flex items-center space-x-2">
            <IconButton
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              size="small"
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                color: currentPage === 1 ? "#D1D5DB" : "#6B7280",
              }}>
              <ChevronLeft size={18} />
            </IconButton>

            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              // Show current page and 1 page before and after
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? "contained" : "outlined"}
                    onClick={() => setCurrentPage(page)}
                    sx={{
                      minWidth: "40px",
                      height: "40px",
                      p: 0,
                      borderRadius: "8px",
                      ...(currentPage === page
                        ? {
                            backgroundColor: "#5652B0",
                            "&:hover": { backgroundColor: "#4B47A1" },
                          }
                        : {
                            borderColor: "#E5E7EB",
                            color: "#6B7280",
                          }),
                    }}>
                    {page}
                  </Button>
                );
              } else if (
                (page === currentPage - 2 && currentPage > 3) ||
                (page === currentPage + 2 && currentPage < totalPages - 2)
              ) {
                return (
                  <span key={page} className="px-2">
                    ...
                  </span>
                );
              }
              return null;
            })}

            <IconButton
              onClick={() =>
                setCurrentPage(prev => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              size="small"
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                color: currentPage === totalPages ? "#D1D5DB" : "#6B7280",
              }}>
              <ChevronRight size={18} />
            </IconButton>
          </div>
        </div>
      )}

      {/* Filter Menu */}
      <Menu
        id="filter-menu"
        anchorEl={filterAnchorEl}
        keepMounted
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            width: 250,
            mt: 1,
            p: 1,
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
          },
        }}>
        <MenuItem
          onClick={() => {
            setTransactionType("all");
            handleFilterClose();
          }}>
          All Transactions
        </MenuItem>
        <MenuItem
          onClick={() => {
            setTransactionType("earn");
            handleFilterClose();
          }}>
          Points Earned Only
        </MenuItem>
        <MenuItem
          onClick={() => {
            setTransactionType("redeem");
            handleFilterClose();
          }}>
          Points Redeemed Only
        </MenuItem>
        <MenuItem
          onClick={() => {
            setTransactionType("adjustment");
            handleFilterClose();
          }}>
          Adjustments Only
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDateRange({ startDate: null, endDate: null });
            handleFilterClose();
          }}>
          Clear Date Filter
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSearchQuery("");
            setTransactionType("all");
            setDateRange({ startDate: null, endDate: null });
            handleFilterClose();
          }}>
          Reset All Filters
        </MenuItem>
      </Menu>
    </div>
  );
};

export default PointsHistory;
